/* ── Downloading a quote's, an invoice's or a credit note's PDF ───────────
 *
 * ⚠️ *SUPERSEDED, the scope: "a quote's or an invoice's PDF".* Credit notes
 * joined on 2026-09-27 (`GET /credit-notes/{id}/pdf`), with a gate of their own
 * — {@link canDownloadCreditNotePdf} — because their read carries no link
 * envelope yet.
 *
 * ONE copy of three decisions, shared by four call sites — `/quotes`,
 * `/quotes/:id`, `/invoices` and `/invoices/:id`. Two copies would agree the day
 * they were written and drift on the first correction, and what would drift is
 * whether a control is OFFERED at all: a page that decided visibility its own
 * way would show a button whose only outcome is a refusal, which is the thing
 * the owner's ruling exists to prevent.
 *
 * The bytes themselves come from `api.downloadQuotePdf` /
 * `api.downloadInvoicePdf`, which go through `requestBinary` in `@/lib/api`, and
 * reach disk through `saveBlob` in `@/lib/download`. Neither is re-implemented
 * here; this module answers only *may I offer it* and *what do I say when it is
 * refused*.
 * ------------------------------------------------------------------------ */

import { ApiError } from './api';
import { linkOf, type MaybeLinked } from './xero';

/**
 * The document has no counterpart in Xero at all — no link row, or a link that
 * has been detached.
 *
 * This is the refusal {@link canDownloadPdf} exists to keep off the screen, so
 * meeting it means the link went stale under a page that was already open.
 */
export const XERO_DOCUMENT_NOT_IN_XERO_CODE = 'XERO_DOCUMENT_NOT_IN_XERO';

/**
 * There is a link, and the document behind it cannot be fetched — the link is
 * `archived`, or Xero answers 404 for the id it holds.
 *
 * ⚠️ **This one is REACHABLE from a correctly-gated control**, which is why the
 * control is not hidden on `archived`. See {@link canDownloadPdf}.
 */
export const XERO_DOCUMENT_GONE_CODE = 'XERO_DOCUMENT_GONE';

/**
 * Xero would not move the quote out of Draft, so nothing was downloaded.
 *
 * 🔴 **THE REFUSAL NOW COSTS MORE THAN IT BUYS, AND THAT IS RECORDED RATHER
 * THAN QUIETLY CHANGED — 2026-09-16.** It was written on the premise that
 * serving the plain bytes after a failed status change would hand somebody the
 * DRAFT-marked document they pressed the control to avoid. **That premise is
 * measured false**: Xero puts no draft marking on a quote's PDF, so the bytes
 * are the same either way and refusing the download buys nothing while costing
 * the person their document. Degrading to the plain bytes instead is a
 * behaviour change nobody has asked for, so the copy below says the recovery is
 * equivalent and the branch is left alone. **Raise it before "fixing" it.**
 *
 * ⚠️ **RAISED BY `POST /quotes/{id}/pdf/mark-sent` ALONE, and never by the
 * plain read.** `GET /quotes/{id}/pdf` is entitled to no opinion about a
 * quote's Xero status — so a reader meeting this code knows without checking
 * that it pressed the WRITE. That is also why it is the one refusal here that
 * can only ever reach a QUOTE: there is no mark-sent route on an invoice or a
 * credit note, because the analogous transition on either posts to the ledger.
 *
 * ⚠️ **It is not `DOCUSIGN_QUOTE_STILL_DRAFT_IN_XERO`**, which the send path
 * raises for the identical Xero failure. That one offers `documentBase64` as
 * the way out; a caller of this route cannot supply its own bytes and would be
 * sent looking for a field that does not exist on this request. Same cause,
 * different recovery, so a different code — the rule
 * {@link XERO_DOCUMENT_GONE_CODE} already states against `XERO_QUOTE_GONE`.
 *
 * ⚠️ **Nothing was downloaded, and the recovery is one control away** — the
 * plain download is still there, open to anyone holding `quotes.export`
 * (mark-sent takes `quotes.send`; *SUPERSEDED: "still open to every role"*,
 * before the 2026-10-02 grants), and now known to
 * produce the identical document. The sentence below says so, because a person
 * told only that the write failed has no way to know the download they wanted
 * is still available and unaffected.
 */
export const XERO_QUOTE_STILL_DRAFT_CODE = 'XERO_QUOTE_STILL_DRAFT';

/**
 * May a Download PDF control be OFFERED for this record?
 *
 * ## Owner's ruling: hidden, not disabled
 *
 * The control is **absent** until the document is in Xero, on the precedent of
 * the Xero `sync` affordance — a button whose only possible outcome is an error
 * is a button nobody should be shown. A disabled one would need a sentence
 * beside it explaining a state that is not a refusal of *this reader*, and the
 * document simply not being in Xero yet is an ordinary state of a new quote.
 *
 * ## ⛔ The link row is the authority, and nothing else is
 *
 * `linkOf(record).id` — never `xeroQuoteNumber`, never `xeroQuoteId`.
 *
 * - `xeroQuoteNumber` is written by a successful push on a **best-effort**
 *   basis, and **Xero renumbers a quote on an update that does not name its
 *   number** (measured QU-0477 → QU-0479 → QU-0478). It once stood populated on
 *   an unlinked quote naming **another quote's document** — which is the PDF
 *   somebody would then download and send to a customer. That is this exact
 *   control, so the mistake is not hypothetical here.
 * - `xeroQuoteId` is written by **nothing at all** — no server path sets it. A
 *   hand-typed value is the only thing it will ever hold, and it looks
 *   authoritative while meaning nothing.
 *
 * ## Why `unlinked` is excluded and `archived` is NOT
 *
 * The test mirrors the server's own `XERO_DOCUMENT_NOT_IN_XERO` condition — no
 * link row, **or** a link whose status is `unlinked` — so the two agree on the
 * state that is hidden. `NO_LINK` satisfies both halves, so a record the backend
 * has not filled the envelope in for is correctly treated as not in Xero rather
 * than crashing.
 *
 * ⚠️ **`archived` deliberately still SHOWS the control**, and the resulting
 * `XERO_DOCUMENT_GONE` is a refusal worth meeting. Hiding it would remove the
 * only place that state gets explained: the recovery for a document deleted in
 * Xero is *unlink here, then create afresh*, which is a real path in this app,
 * and a control that silently vanishes cannot say so. A refusal that names a
 * next step beats an absence that names nothing.
 *
 * **It is an optimisation, not a guarantee.** The link can be detached by
 * somebody else while this page sits open, so every caller must still handle
 * both 409s — {@link pdfRefusalMessage} is what words them.
 */
export function canDownloadPdf(record: MaybeLinked | null | undefined): boolean {
  const link = linkOf(record);
  return link.id !== null && link.status !== 'unlinked';
}

/** The three documents this app downloads a Xero PDF for. */
export type PdfDocumentKind = 'quote' | 'invoice' | 'credit-note';

/** How each kind is named inside a sentence. A slug is not a word. */
const DOCUMENT_WORD: Record<PdfDocumentKind, string> = {
  quote: 'quote',
  invoice: 'invoice',
  'credit-note': 'credit note',
};

export function pdfDocumentWord(kind: PdfDocumentKind): string {
  return DOCUMENT_WORD[kind];
}

/**
 * What {@link canDownloadCreditNotePdf} reads off a credit note. Declared here
 * rather than taking `CreditNote`, so the gate names exactly the fields it
 * depends on and nothing else.
 */
export interface CreditNotePdfGate extends MaybeLinked {
  importedFromXero: boolean;
  approvedAt: string | null;
}

/**
 * May a Download PDF control be OFFERED for this credit note?
 *
 * 🔴 **THE CREDIT-NOTE READ CARRIES NO `xero` LINK ENVELOPE** (measured
 * 2026-09-27: `getCreditNote` returns `toCreditNote(row)` and nothing joins
 * `f_xero_links`), so {@link canDownloadPdf} alone would answer `false` on
 * every note and the control would never render. When the envelope arrives,
 * the first branch below takes over and this becomes the ordinary link-row
 * test — nothing at a call site has to change.
 *
 * Until then the fallback hides the control ONLY where the note is provably
 * not in Xero, and offers it everywhere else:
 *
 * - **Imported from Xero** — the importer writes the link row, so it is there.
 * - **Approved at some point** (`approvedAt` is not cleared by voiding) — the
 *   push runs on APPROVE and skips every unapproved note (backend
 *   `pushCreditNote`, `skipped` on `!spendable`), so an approved note has had
 *   its push attempted.
 * - **Neither** — an EMERGE-raised note that was never approved has never been
 *   pushed, so the only outcome of the control would be a refusal. Hidden, on
 *   the same ruling as {@link canDownloadPdf}.
 *
 * ⛔ **NEVER `xeroCreditNoteNumber` or `xeroCreditNoteId`** — the importer
 * writes neither, and a number is a label rather than a link.
 *
 * ⚠️ **An approved note whose push failed still shows the control**, and meets
 * `XERO_DOCUMENT_NOT_IN_XERO`. That is the enable-when-in-doubt direction: a
 * refusal that names the state beats hiding a document that may well exist.
 */
export function canDownloadCreditNotePdf(note: CreditNotePdfGate | null | undefined): boolean {
  if (!note) return false;
  if (note.xero !== undefined && note.xero !== null) return canDownloadPdf(note);
  return note.importedFromXero || note.approvedAt !== null;
}

/** Longest reference stem the fallback name carries — the server caps its own at 120. */
const FALLBACK_STEM_LIMIT = 120;

/**
 * The name a credit note's PDF is saved under when the server's
 * `Content-Disposition` is unreadable.
 *
 * Built from the note's reference (`creditNoteRef` — Xero's number, else the
 * SUBJECT), never from the id: a credit note has no number of its own, and a
 * file named after a primary key is one nobody recognises on their own disk.
 * `saveBlob` sanitises the characters; this only bounds the length, because a
 * subject may be 255 characters and a path component may not.
 */
export function creditNotePdfFallbackName(reference: string): string {
  const stem = reference.trim().slice(0, FALLBACK_STEM_LIMIT).trim();
  return `${stem || 'credit-note'}.pdf`;
}

/**
 * The sentence to show when a download is refused.
 *
 * The two shared 409s mean opposite things and send a reader to opposite
 * places, so they are worded separately: one says the document was never sent,
 * the other says it was and is no longer retrievable. Rendering them alike
 * would tell somebody to go and look in Xero for a document that has never been
 * there.
 *
 * ⚠️ **A third, {@link XERO_QUOTE_STILL_DRAFT_CODE}, is reachable only from the
 * mark-sent WRITE** — the plain read never raises it. It is worded here anyway
 * rather than in the component, so the one place this app decides what a refused
 * PDF says stays one place.
 *
 * ⛔ **There is deliberately no 403 branch.** A `viewer` is refused the
 * mark-sent route, but the control is WITHHELD from a reader who cannot write,
 * so a 403 arriving here is a race — a role changed under a page already open,
 * or a bundle older than the grant — and not an ordinary state with a next step
 * this module could name. It falls through to the server's own sentence, which
 * is about that reader rather than about the document. Adding a branch would
 * put a permanent, confident refusal sentence in front of a state nobody should
 * be able to reach, and would read as this app inventing a restriction.
 *
 * ⚠️ **Anything else falls through to the server's own message**, deliberately.
 * An unrecognised code means "newer than this branch", never "not a refusal" —
 * the same rule `quoteRefusalOf` runs on. `ApiError.message` is never empty (its
 * constructor falls back to a sentence naming the status), so this always
 * returns something a reader can act on rather than an empty banner.
 *
 * A non-`ApiError` is a transport failure — the request never reached the
 * server — and says so rather than borrowing a refusal's wording.
 */
export function pdfRefusalMessage(error: unknown, kind: PdfDocumentKind): string {
  const documentWord = pdfDocumentWord(kind);

  if (!(error instanceof ApiError)) {
    return `The ${documentWord} PDF could not be fetched. Check your connection and try again.`;
  }

  if (error.code === XERO_DOCUMENT_NOT_IN_XERO_CODE) {
    return `This ${documentWord} is not in Xero, so there is no PDF to download. Send it to Xero first.`;
  }

  if (error.code === XERO_DOCUMENT_GONE_CODE) {
    return `The Xero document for this ${documentWord} can no longer be reached. Unlink it and create it afresh in Xero.`;
  }

  /*
   * Only ever a quote — see the constant. `documentWord` is not interpolated
   * here for that reason: an invoice cannot reach this branch, and a sentence
   * that could word itself as one would outlive the fact that keeps it true.
   */
  if (error.code === XERO_QUOTE_STILL_DRAFT_CODE) {
    return 'This quote could not be marked Sent in Xero, so nothing was downloaded. Use Download PDF instead — it gives the same document — or change the status in Xero.';
  }

  return error.message;
}

/* ── The confirmation before marking a quote Sent in Xero ───────────────────
 *
 * The wording lives HERE, beside the refusal sentences, and not in the
 * component that renders it — the same reason `pdfRefusalMessage` does. What
 * this dialog says is a claim about a WRITE into somebody else's accounting
 * system that this app cannot undo, and a claim of that weight should not be
 * one component's private string.
 *
 * ⛔ **This is body text naming a CONSEQUENCE, which is one of the documented
 * exceptions to the no-helper-text prohibition — not a carve-out from it.** It
 * survives a sweep because it names a state the reader cannot otherwise see:
 * nothing on this screen shows the Xero status, and the write is irreversible
 * from here. **That licence does not extend past this dialog.** No hint on the
 * control, no sentence under it.
 * ------------------------------------------------------------------------ */

/**
 * ⚠️ **Phrased as a question about the ACTION, never as a claim about this
 * quote's state.** Nothing on the EMERGE record carries the Xero status, so
 * "This quote is a draft" is a sentence this app is not entitled to write.
 *
 * 🔴 **"in Xero" WAS DROPPED — 2026-09-16.** *SUPERSEDED: "Mark this quote Sent
 * in Xero?"* It was precise when the write reached Xero and nothing else, and
 * it became wrong the moment the route started moving the EMERGE stage too: a
 * title naming one of two systems reads as a promise that the other is left
 * alone. The quote's own STAGE used to be "a different fact that legitimately
 * disagrees"; it is now the same fact, which is the whole of the fix.
 */
export const MARK_SENT_DIALOG_TITLE = 'Mark this quote Sent?';

/**
 * 🔴 **REWRITTEN 2026-09-16, BECAUSE ITS FIRST SENTENCE WAS MEASURED FALSE IN
 * PRODUCTION.** *SUPERSEDED: "A quote that is still a draft in Xero downloads
 * with DRAFT across it. Marking it Sent in Xero removes that and gives you the
 * document that goes for signature."*
 *
 * **Xero puts no draft marking on a quote's PDF at all.** Measured on the
 * owner's own press: QU-0540 came back at 39,995 bytes as a draft and 40,006
 * bytes as Sent forty-four seconds later — the same document — and QU-0538,
 * a draft in Xero at the time, rendered with no DRAFT wording or watermark
 * anywhere on it. The DRAFT watermark is a Xero **invoice** behaviour. So the
 * copy promised a change to the document that could never happen, and the
 * owner reported exactly that: *"still i am getting the downloaded pdf as the
 * draft quote one"* — because it IS the same one.
 *
 * ⚠️ **What made their PDF look wrong was the BRANDING THEME, not the status**
 * — QU-0540 sits on a Xero theme with no logo configured. That is a Xero
 * setting and nothing this sentence may claim to fix.
 *
 * 🔴 **AND IT MOVES THE EMERGE STAGE NOW — 2026-09-16.** *SUPERSEDED: "records
 * in Xero that the quote has gone out".* Owner's report: *"it does not change
 * from draft to sent in emerge and xero after clicking that button in modal"*.
 * Measured — QU-0540 and QU-0542 read SENT in Xero and **Draft in EMERGE**,
 * while a quote sent for signature read Sent in both, because that path moved
 * the stage and this one never did. The route now calls the same function, so
 * this sentence has to name both systems or it under-reports a write.
 *
 * Three things, and each is load-bearing:
 *
 * 1. **What it actually does**: moves the quote to Sent in EMERGE **and** in
 *    Xero — the same move a signature request makes.
 * 2. 🔴 **That the PDF is the same either way.** Without it somebody presses
 *    the control expecting a different document, does not get one, and reports
 *    the feature as broken — which is precisely what happened.
 * 3. 🔴 **That the XERO half cannot be undone from here.** ⚠️ Worded as the
 *    Xero half specifically, because the EMERGE stage *can* be changed back on
 *    the quote itself — a blanket "EMERGE cannot undo this" would now be false
 *    about one of the two writes, and false in the direction that stops
 *    somebody fixing a mistake they are allowed to fix.
 *
 * ⛔ Do **not** add "it does not email the customer": that is a claim about
 * what Xero does next, it has never been measured from here, and it would be a
 * reassurance about somebody else's system on the one screen where being wrong
 * costs a customer an email nobody expected.
 *
 * 🔴 **REWRITTEN 2026-09-24 — owner's decision, and it adds a FOURTH point.**
 * *SUPERSEDED: "This moves the quote to Sent in both EMERGE and Xero, recording
 * that it has gone to the customer — the same move a signature request makes.
 * The PDF is the same either way: Xero does not mark a quote as a draft on the
 * document. The Xero half cannot be undone from here; that status would have to
 * be changed in Xero afterwards."*
 *
 * 4. ~~That marking Sent LOCKS the quote in Xero.~~ 🔴 **WITHDRAWN 2026-10-25
 *    — it was already false when written.** *SUPERSEDED: "… and locks it in
 *    Xero: any line edits you make afterwards will not reach the Xero quote or
 *    the PDF the customer receives."* Backend 50175a4 (2026-09-18) widened
 *    `XERO_STATUSES_A_PUSH_MAY_REWRITE` in `xero-quote.service.ts` to
 *    `{DRAFT, SENT}`, so a save still re-pushes the lines to a quote Xero holds
 *    as SENT; only ACCEPTED, DECLINED, INVOICED, EXPIRED and DELETED are
 *    refused. Marking Sent therefore locks nothing, and the sentence now says
 *    only what the write does. Do not reinstate a lock claim unless that set
 *    shrinks back.
 *
 * Also: user-facing copy names the app "SEED EMERGE", never "EMERGE".
 */
export const MARK_SENT_DIALOG_BODY =
  'The PDF is the same either way. Marking it Sent moves the quote to Sent in SEED EMERGE and Xero. The Xero half cannot be undone from here.';

/**
 * The confirming action: marks the quote Sent in Xero **and** downloads.
 *
 * ⚠️ It names both halves because both happen. "Mark Sent" alone hides the
 * download and reads as a status control; "Download" alone hides the write, on
 * the one button in this app that changes a record in the accountant's Xero.
 */
export const MARK_SENT_CONFIRM_LABEL = 'Mark Sent and download';

/**
 * The way out that is not a cancel: download the PDF and change nothing in
 * Xero.
 *
 * 🔴 **RENAMED 2026-09-16.** *SUPERSEDED: "Download as it stands".* That
 * phrasing was chosen to avoid claiming the document carried a watermark —
 * careful about the right thing, and still wrong: **"as it stands" implies the
 * two buttons hand back different documents, and they do not.** It named the
 * difference as being in the FILE when the only difference is the STATUS, so
 * the label quietly restated the very promise the body text has now had to
 * withdraw.
 *
 * ⚠️ **The label names what it does NOT do**, because that is the whole of the
 * choice on offer here. It is the {@link canDownloadPdf} route, open to every
 * role, and it is also the recovery offered when the write is refused.
 */
export const MARK_SENT_PLAIN_LABEL = 'Download without marking it Sent';
